// @ts-nocheck

import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import ResizeDetector from 'react-resize-detector';

import { Target } from '../client';
import usePrevious from '../hooks/usePrevious';

/*
  ## CAVEATS ##
  * There is an issue with inline sibling like <span>
  * Sibling must be an instance of HTMLElement otherwise it will be ignored
  * Sibling must be in position static/relative otherwise it will be ignored
*/

const FitRemainingHeight = ({ children, className, updateKey }) => {
  const mountedRef = useRef();
  const rootElRef = useRef(null);
  const resizeElRef = useRef(null);
  const timeoutIdRef = useRef(null);

  const [remainingHeight, setRemainingHeight] = useState(0);
  const [siblingCount, setSiblingCount] = useState(0);
  const previousSiblingCount = usePrevious(siblingCount);
  const previousUpdateKey = usePrevious(updateKey);

  const computeLayout = useCallback(() => {
    if (rootElRef?.current) {
      const parent = rootElRef.current.parentElement;
      const parentHeight = parseInt(window.getComputedStyle(parent).height, 10);
      const siblings = getSiblings().map((sibling) => [sibling, window.getComputedStyle(sibling)]);
      const computedSiblingHeight = siblings
        .filter((x) => ['static', 'relative'].indexOf(x[1].position) !== -1)
        .reduce((result, current) => result + parseInt(current[0].offsetHeight, 10), 0);

      setRemainingHeight(!parentHeight ? 0 : Math.max(0, parentHeight - computedSiblingHeight));
      setSiblingCount(siblings.length);
    }
  }, [rootElRef]);

  const invalidateLayout = useCallback(() => {
    clearTimeout(timeoutIdRef.curent);
    timeoutIdRef.current = setTimeout(() => computeLayout(), 200);
  }, [computeLayout]);

  const getSiblings = () => {
    if (rootElRef.current) {
      const siblings = [];
      let el = rootElRef.current.parentElement.firstChild;
      while (el) {
        if (el instanceof HTMLElement) {
          if (el !== rootElRef.current && el !== resizeElRef) {
            siblings.push(el);
          }
        }
        el = el.nextSibling;
      }
      return siblings;
    }
    return [];
  };

  const onResize = () => invalidateLayout();

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      computeLayout();
    } else {
      const siblings = getSiblings();

      if (previousSiblingCount > siblings.length) {
        computeLayout();
      } else {
        const siblingsHasChanged = previousSiblingCount !== siblings.length;
        const updateKeyHasChanged = previousUpdateKey !== updateKey;
        if (siblingsHasChanged || updateKeyHasChanged) {
          invalidateLayout();
        }
      }
    }
  }, [computeLayout, invalidateLayout, previousSiblingCount, previousUpdateKey, updateKey]);

  return [
    <div key="root" className={className} ref={rootElRef}>
      {children({ remainingHeight, siblingCount })}
    </div>,
    <ResizeDetector key="resize" handleHeight onResize={onResize} />,
  ];
};

if (Target.__ENV__ !== 'production') {
  FitRemainingHeight.propTypes = {
    children: PropTypes.func.isRequired,
  };
}

export default FitRemainingHeight;
